import { useState } from 'react';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Laptop, LogOut, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toaster';

interface SessionRow {
  sid: string;
  createdAt: string;
  // Updated opportunistically by requireAuth (~5 min granularity).
  // Null for sessions created before the field was introduced.
  lastSeenAt: string | null;
  ip: string | null;
  userAgent: string | null;
  isCurrent: boolean;
}

const sessionsQueryOptions = queryOptions({
  queryKey: ['account', 'sessions'] as const,
  queryFn: () => api<{ items: SessionRow[] }>(`/api/admin/auth/sessions`).then((d) => d.items),
  staleTime: 30_000,
});

function shortenUa(ua: string): string {
  const browser = /(Edg|Chrome|Firefox|Safari)\/[\d.]+/.exec(ua)?.[1] ?? ua.split(/\s/)[0] ?? '';
  const platform = /\(([^)]+)\)/.exec(ua)?.[1]?.split(';')[0]?.trim() ?? '';
  return [browser, platform].filter(Boolean).join(' / ');
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  const day = Math.round(hr / 24);
  return `${day} วันที่แล้ว`;
}

// Bangkok timezone — admin team is Thailand-based and the original
// session is almost always started from a Thai-local device. Exact
// time helps an investigator cross-reference with login activity
// rows above (which also use Asia/Bangkok via the same formatter).
const EXACT_FMT = new Intl.DateTimeFormat('th-TH', {
  dateStyle: 'medium',
  timeStyle: 'medium',
  timeZone: 'Asia/Bangkok',
});

/**
 * Active session manager — lists every KV session record owned by
 * the current user with sign-out buttons. Revoking the current
 * session is allowed but immediately invalidates the cookie, so on
 * success we reload to bounce the user back through /login.
 */
export function SessionsSection() {
  const query = useQuery(sessionsQueryOptions);
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: (sid: string) =>
      api<{ ok: true; wasCurrent: boolean }>(`/api/admin/auth/sessions/${sid}`, {
        method: 'DELETE',
      }),
    onSuccess: (data) => {
      if (data.wasCurrent) {
        // Current session is gone — hard reload bounces through the
        // auth gate and into /login. Skip the cache invalidate, no
        // point updating data we can't display.
        toast.success('ออกจากระบบเครื่องนี้');
        setTimeout(() => {
          window.location.href = '/login';
        }, 600);
        return;
      }
      void qc.invalidateQueries({ queryKey: ['account', 'sessions'] });
      toast.success('สิ้นสุดเซสชันแล้ว');
    },
    onError: () => toast.error('สิ้นสุดเซสชันไม่สำเร็จ'),
  });

  const items = query.data ?? [];

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
      <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-slate-50">
        <Laptop className="h-4 w-4 text-slate-400" />
        เซสชันที่ยังใช้งาน
      </h3>
      <p className="mb-4 text-xs text-slate-500">
        เห็นเครื่องที่จำไม่ได้? สิ้นสุดเซสชันนั้น (อุปกรณ์ดังกล่าวต้องเข้าสู่ระบบใหม่)
      </p>
      {query.isLoading ? (
        <p className="text-sm text-slate-500">กำลังโหลด...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">ไม่มีเซสชันที่ใช้งานอยู่</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((s) => {
            const isOpen = expanded === s.sid;
            return (
              <li key={s.sid} className="py-3 text-xs">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'ซ่อนรายละเอียดเซสชัน' : 'ดูรายละเอียดเซสชัน'}
                    onClick={() => setExpanded(isOpen ? null : s.sid)}
                    className="mt-0.5 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                      {s.userAgent ? shortenUa(s.userAgent) : 'ไม่ทราบเครื่อง'}
                      {s.isCurrent && (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          เครื่องนี้
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-slate-400">
                      {s.ip ? `IP ${s.ip}` : 'ไม่มี IP'} · เริ่ม {formatRelative(s.createdAt)}
                      {s.lastSeenAt && ` · ใช้งาน ${formatRelative(s.lastSeenAt)}`}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(s.sid)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                  >
                    {s.isCurrent ? (
                      <LogOut className="mr-1 h-3.5 w-3.5" />
                    ) : (
                      <ShieldOff className="mr-1 h-3.5 w-3.5" />
                    )}
                    {s.isCurrent ? 'ออกจากเครื่องนี้' : 'สิ้นสุด'}
                  </Button>
                </div>
                {isOpen && (
                  <dl className="ml-6 mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] dark:border-slate-800 dark:bg-slate-800/40">
                    <dt className="text-slate-500">เริ่มเวลา</dt>
                    <dd className="font-mono text-slate-700 dark:text-slate-300">
                      {EXACT_FMT.format(new Date(s.createdAt))}
                    </dd>
                    <dt className="text-slate-500">ใช้งานล่าสุด</dt>
                    <dd className="font-mono text-slate-700 dark:text-slate-300">
                      {s.lastSeenAt ? (
                        <>
                          {EXACT_FMT.format(new Date(s.lastSeenAt))}
                          <span className="ml-1 text-slate-400">
                            ({formatRelative(s.lastSeenAt)})
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </dd>
                    <dt className="text-slate-500">User-Agent</dt>
                    <dd className="break-all font-mono text-slate-700 dark:text-slate-300">
                      {s.userAgent ?? '—'}
                    </dd>
                    <dt className="text-slate-500">Session ID</dt>
                    <dd className="font-mono text-slate-700 dark:text-slate-300">
                      {s.sid.slice(0, 12)}…
                      <span className="ml-1 text-slate-400">
                        (cross-ref audit metadata.revokedSid)
                      </span>
                    </dd>
                  </dl>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
